import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Snowflake, Undo2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { listRevocations, applyRevocationFreeze, rebuildRevocationDownstream } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";

const RISK = {
  high: "bg-rose-50 text-rose-800 border-rose-200",
  medium: "bg-amber-50 text-amber-900 border-amber-200",
  low: "bg-slate-50 text-slate-700 border-slate-200",
};

const REBUILD_STATUS = {
  rebuilt: "completed",
  in_progress: "in_review",
};
const rebuildBadgeValue = (s) => REBUILD_STATUS[s] || "new";

export default function Revocations() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    listRevocations()
      .then((r) => setItems(r.items || []))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load revocations"));
  }, []);

  function updateItem(id, updated) {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
  }

  async function handleFreeze(r) {
    if (r.freeze_applied) return;
    try {
      const res = await applyRevocationFreeze(r.id);
      updateItem(r.id, res.revocation);
      toast.success(`Freeze applied to ${r.id}`);
    } catch {
      toast.error("Failed to apply freeze");
    }
  }

  async function handleRebuild(r) {
    try {
      const res = await rebuildRevocationDownstream(r.id);
      updateItem(r.id, res.revocation);
      toast.success(`Rebuild triggered for ${r.id}`);
    } catch {
      toast.error("Failed to trigger rebuild");
    }
  }

  return (
    <div className="p-8 md:p-10 space-y-6" data-testid="revocations-page">
      <div>
        <div className="tracked-caps">Safety</div>
        <h2 className="mt-1 font-serif-display text-3xl">Revocations</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Contested or removed knowledge is frozen first, then rebuilt with downstream impact analysis.
          Every revocation is written to the evidence record.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { label: "Active revocations", value: items.filter((i) => i.status !== "completed").length },
          { label: "Completed this month", value: items.filter((i) => i.status === "completed").length },
          { label: "High-risk", value: items.filter((i) => i.risk_level === "high").length, tone: "text-rose-700" },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-border bg-card p-5">
            <div className="tracked-caps">{k.label}</div>
            <div className={`mt-3 font-serif-display text-3xl ${k.tone || ""}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((r) => (
          <article key={r.id} className="rounded-lg border border-border bg-card p-5" data-testid={`revocation-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
                  <StatusBadge value={r.status} />
                  <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider ${RISK[r.risk_level]}`}>
                    <AlertTriangle className="h-3 w-3" /> {r.risk_level} risk
                  </span>
                </div>
                <h3 className="mt-2 font-serif-display text-xl leading-snug">{r.artifact_title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{r.reason}</p>
              </div>
              <div className="text-right text-xs text-muted-foreground font-mono shrink-0">
                <div>Initiated by</div>
                <div className="text-foreground text-sm font-sans">{r.initiated_by}</div>
                <div className="mt-1">{fmtDate(r.initiated_at)} · {fmtTime(r.initiated_at)}</div>
              </div>
            </div>

            <div className="mt-4 grid md:grid-cols-3 gap-4">
              <Detail k="Affected targets" v={(r.affected_targets || []).join(", ")} />
              <Detail k="Downstream artifacts" v={<span className="font-mono">{r.downstream_artifacts}</span>} />
              <Detail k="Rebuild" v={<StatusBadge value={rebuildBadgeValue(r.rebuild_status)} />} />
            </div>

            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={() => handleFreeze(r)}
                disabled={r.freeze_applied}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-60 disabled:cursor-default"
                data-testid={`freeze-${r.id}`}
              >
                <Snowflake className="h-3.5 w-3.5" /> {r.freeze_applied ? "Frozen" : "Apply freeze"}
              </button>
              <button
                onClick={() => handleRebuild(r)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
              >
                <Undo2 className="h-3.5 w-3.5" /> Rebuild downstream
              </button>
              <button
                onClick={() => navigate(`/app/evidence?artifact=${r.artifact_id}`)}
                className="inline-flex items-center gap-1 ml-auto text-xs text-brand hover:underline"
              >
                Open audit trail <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="tracked-caps">{k}</div>
      <div className="mt-1 text-sm">{v}</div>
    </div>
  );
}
